// src/components/dashboard/CharacterInfo.js
// eslint-disable-next-line no-unused-vars
import React, {useState, useEffect} from 'react';
import styled from 'styled-components';
import {processTooltip} from '../../../utils/tooltipHelper';
import {getAllCharacters, getActiveServers} from '../../../api/axios';
import {useTranslation} from 'react-i18next';
import {useAuth} from '../../../hooks/useAuth';
import {Navigate} from 'react-router-dom';
import ServerSwitch from '../serverSwitch';

const CharacterInfo = () => {
  const {t} = useTranslation();
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active'); // "active" oder "deleted"
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const {user} = useAuth();

  const [sv, setSv] = useState('hybrid');
  const [activeServers, setActiveServers] = useState([]);
  const showDropdown = activeServers.length > 1;

  useEffect(() => {
    let mounted = true;
    getActiveServers()
      .then(res => {
        if (!mounted) return;
        const list = Array.isArray(res?.data?.servers) ? res.data.servers : [];
        setActiveServers(list);
        // genau 1 aktiv -> automatisch setzen
        if (list.length === 1 && list[0]?.friendly_key) setSv(list[0].friendly_key);
        // mehrere aktiv, aktueller sv nicht dabei -> ersten aktiven setzen
        if (list.length > 1 && !list.find(s => s.friendly_key === sv) && list[0]?.friendly_key) {
          setSv(list[0].friendly_key);
        }
      })
      .catch(() => setActiveServers([]));
    return () => {
      mounted = false;
    };
  }, [sv]);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => {
      getAllCharacters(sv)
        .then(response => {
          let data = response.data;
          // Falls die API-Daten kein Array liefern, in ein Array umwandeln
          if (!Array.isArray(data)) {
            data = Object.values(data);
          }
          setCharacters(data);
          setLoading(false);
        })
        .catch(error => {
          console.error('Error fetching characters:', error);
          setLoading(false);
        });
    }, 1000); // 1000ms Delay

    return () => clearTimeout(timer);
  }, [sv]);

  // Filtert Charaktere basierend auf delete_char
  const filteredCharacters = characters.filter(char =>
    activeTab === 'active' ? char.delete_char === '0' : char.delete_char !== '0'
  );

  if (!user) {
    return <Navigate to="/" replace />;
  }

  // Hilfsfunktion, um alle vorhandenen Titel als kommagetrennten String zusammenzufügen
  const getTitlesString = char => {
    const titles = [];
    if (char.main_title_value && char.main_title_value.trim() !== '') titles.push(char.main_title_value);
    if (char.sub_title_0_value && char.sub_title_0_value.trim() !== '') titles.push(char.sub_title_0_value);
    if (char.sub_title_1_value && char.sub_title_1_value.trim() !== '') titles.push(char.sub_title_1_value);
    if (char.sub_title_2_value && char.sub_title_2_value.trim() !== '') titles.push(char.sub_title_2_value);
    if (char.sub_title_3_value && char.sub_title_3_value.trim() !== '') titles.push(char.sub_title_3_value);
    if (char.sub_title_4_value && char.sub_title_4_value.trim() !== '') titles.push(char.sub_title_4_value);
    return titles.join(' * ');
  };

  // Wiederherstellungsfunktion (Platzhalter)
  const handleRestore = (charId, e) => {
    e.stopPropagation();
  };

  // Helfer: Belt-Icons (belt_00_face bis belt_07_face)
  const renderBeltIcons = char => {
    const beltIcons = [];
    for (let i = 0; i < 8; i++) {
      const beltKey = `belt_0${i}_face`;
      const beltIcon = char[beltKey];
      if (beltIcon && beltIcon.trim() !== '') {
        beltIcons.push(<BeltIcon key={beltKey} src={`/assets/items/${beltIcon}`} alt={`Belt ${i}`} />);
      }
    }
    return beltIcons;
  };

  // Helfer: Summons – nur Icon, mit Tooltip für Name, Level und Pct
  const renderSummons = char => {
    const summons = [];
    // Main Summon
    if (char.main_summon_name && char.main_summon_name.trim() !== '') {
      summons.push(
        <SummonItem key="main">
          <SummonIcon
            src={`/assets/items/${char.main_summon_face ? char.main_summon_face : 'unknown.jpg'}`}
            alt={char.main_summon_name}
          />
          <Tooltip className="tooltip">
            <div>
              <strong>Main:</strong> {char.main_summon_name}
            </div>
            <div>Level: {char.main_summon_lv}</div>
            <div>Pct: {char.main_summon_pct}</div>
          </Tooltip>
        </SummonItem>
      );
    }
    // Sub Summon
    if (char.sub_summon_name && char.sub_summon_name.trim() !== '') {
      summons.push(
        <SummonItem key="sub">
          <SummonIcon
            src={`/assets/items/${char.sub_summon_face ? char.sub_summon_face : 'unknown.jpg'}`}
            alt={char.sub_summon_name}
          />
          <Tooltip className="tooltip">
            <div>
              <strong>Sub:</strong> {char.sub_summon_name}
            </div>
            <div>Level: {char.sub_summon_lv}</div>
            <div>Pct: {char.sub_summon_pct}</div>
          </Tooltip>
        </SummonItem>
      );
    }
    // Zusätzliche Summons: summon_0 bis summon_5
    for (let i = 0; i <= 5; i++) {
      const nameKey = `summon_${i}_name`;
      if (char[nameKey] && char[nameKey].trim() !== '') {
        const faceKey = `summon_${i}_face`;
        const lvKey = `summon_${i}_lv`;
        const pctKey = `summon_${i}_pct`;
        summons.push(
          <SummonItem key={`summon_${i}`}>
            <SummonIcon src={`/assets/items/${char[faceKey] ? char[faceKey] : 'unknown.jpg'}`} alt={char[nameKey]} />
            <Tooltip className="tooltip">
              <div>
                <strong>{char[nameKey]}</strong>
              </div>
              <div>Level: {char[lvKey]}</div>
              <div>Pct: {char[pctKey]}</div>
            </Tooltip>
          </SummonItem>
        );
      }
    }
    return summons;
  };

  // Sortiert die Items nur nach wear_info und rendert sie in einer Grid-Ansicht.
  const renderItemsByWearInfo = items => {
    const sortedItems = items.slice().sort((a, b) => Number(a.wear_info) - Number(b.wear_info));
    return (
      <EquipmentGrid>
        {sortedItems.map((item, idx) => (
          <EquipmentItem key={`${selectedCharacter.sid}-item-${idx}`}>
            <ItemIcon
              src={`/assets/items/${item.item_icon ? item.item_icon + '.jpg' : 'unknown.jpg'}`}
              alt={item.item_name}
            />
            <Tooltip className="tooltip" dangerouslySetInnerHTML={{__html: processTooltip(item)}} />
          </EquipmentItem>
        ))}
      </EquipmentGrid>
    );
  };

  return (
    <StyledDiv>
      <TopHeader>
        <TabButton
          active={activeTab === 'active'}
          onClick={() => {
            setActiveTab('active');
            setSelectedCharacter(null);
          }}
        >
          {t('characterInfo.tab.active')}
        </TabButton>
        <TabButton
          active={activeTab === 'deleted'}
          onClick={() => {
            setActiveTab('deleted');
            setSelectedCharacter(null);
          }}
        >
          {t('characterInfo.tab.deleted')}
        </TabButton>
      </TopHeader>
      {showDropdown && (
        <div style={{display: 'flex', justifyContent: 'flex-start', marginBottom: 12}}>
          <ServerSwitch value={sv} onChange={setSv} label="Server" />
        </div>
      )}
      <MainContainer>
        <Sidebar>
          {loading ? (
            <p>{t('characterInfo.loading')}</p>
          ) : filteredCharacters.length === 0 ? (
            <p>{t('characterInfo.noCharactersFound')}</p>
          ) : (
            <CharacterList>
              {filteredCharacters.map((char, index) => (
                <CharacterButton
                  key={char.sid || index}
                  onClick={() => setSelectedCharacter(char)}
                  selected={selectedCharacter && selectedCharacter.sid === char.sid}
                >
                  <JobIcon src={`/assets/jobs/${char.job_icon ? char.job_icon : '000.jpg'}`} alt={char.job_text} />
                  <CharacterName>{char.name}</CharacterName>
                </CharacterButton>
              ))}
            </CharacterList>
          )}
        </Sidebar>
        <DetailPanel>
          {selectedCharacter ? (
            <>
              <DetailHeader>
                <JobIconLarge
                  src={`/assets/jobs/${selectedCharacter.job_icon ? selectedCharacter.job_icon : '000.jpg'}`}
                  alt={selectedCharacter.job_text}
                />
                <DetailHeaderInfo>
                  <DetailTitle>{selectedCharacter.name}</DetailTitle>
                  <DetailSubInfo>
                    <span>
                      {t('characterInfo.level')}: {selectedCharacter.lv}
                    </span>{' '}
                    |{' '}
                    <span>
                      {t('characterInfo.exp')}: {selectedCharacter.exp_pct}
                    </span>
                  </DetailSubInfo>
                  {getTitlesString(selectedCharacter) && (
                    <DetailTitles>{getTitlesString(selectedCharacter)}</DetailTitles>
                  )}
                </DetailHeaderInfo>
                {activeTab === 'deleted' && (
                  <RestoreButton onClick={e => handleRestore(selectedCharacter.sid, e)}>
                    {t('characterInfo.requestRestoration')}
                  </RestoreButton>
                )}
              </DetailHeader>
              <Section>
                <SectionTitle>{t('characterInfo.generalInformation')}</SectionTitle>
                <InfoGrid>
                  <InfoItem>
                    <strong>{t('characterInfo.job')}:</strong> {selectedCharacter.job_text}
                  </InfoItem>
                  <InfoItem>
                    <strong>{t('characterInfo.gold')}:</strong> {selectedCharacter.gold}
                  </InfoItem>
                  <InfoItem>
                    <strong>{t('characterInfo.pkc')}:</strong> {selectedCharacter.pkc}
                  </InfoItem>
                  <InfoItem>
                    <strong>{t('characterInfo.sex')}:</strong> {selectedCharacter.sex}
                  </InfoItem>
                  <InfoItem>
                    <strong>{t('characterInfo.arenaPoints')}:</strong> {selectedCharacter.arena_point}
                  </InfoItem>
                  <InfoItem>
                    <strong>{t('characterInfo.cha')}:</strong> {selectedCharacter.cha}
                  </InfoItem>
                  <InfoItem>
                    <strong>{t('characterInfo.jlv')}:</strong> {selectedCharacter.jlv}
                  </InfoItem>
                  <InfoItem>
                    <strong>{t('characterInfo.jp')}:</strong> {selectedCharacter.jp}
                  </InfoItem>
                  <InfoItem>
                    <strong>{t('characterInfo.huntaholicPoints')}:</strong> {selectedCharacter.huntaholic_point}
                  </InfoItem>
                  <InfoItem>
                    <strong>{t('characterInfo.immoralPoints')}:</strong> {selectedCharacter.immoral_point}
                  </InfoItem>
                  <InfoItem>
                    <strong>{t('characterInfo.playTime')}:</strong> {selectedCharacter.play_time_formatted}
                  </InfoItem>
                  <InfoItem>
                    <strong>{t('characterInfo.createTime')}:</strong> {selectedCharacter.create_time}
                  </InfoItem>
                  {activeTab === 'deleted' && (
                    <InfoItem>
                      <strong>{t('characterInfo.deleteTime')}:</strong> {selectedCharacter.delete_time}
                    </InfoItem>
                  )}
                </InfoGrid>
              </Section>
              <SectionItems>
                <SectionTitle>{t('characterInfo.items')}</SectionTitle>
                {selectedCharacter.items && selectedCharacter.items.length > 0 ? (
                  renderItemsByWearInfo(selectedCharacter.items)
                ) : (
                  <p>{t('characterInfo.noItems')}</p>
                )}
              </SectionItems>
              <Section>
                <SectionTitle>{t('characterInfo.belt')}</SectionTitle>
                <BeltContainer>{renderBeltIcons(selectedCharacter)}</BeltContainer>
              </Section>
              <Section>
                <SectionTitle>{t('characterInfo.summons')}</SectionTitle>
                <SummonsGrid>{renderSummons(selectedCharacter)}</SummonsGrid>
              </Section>
            </>
          ) : (
            <PlaceholderText>{t('characterInfo.selectCharacterPlaceholder')}</PlaceholderText>
          )}
        </DetailPanel>
      </MainContainer>
    </StyledDiv>
  );
};

export default CharacterInfo;

/* Styled Components */
const StyledDiv = styled.div``;

const MainContainer = styled.div`
  display: flex;
  height: 100%;
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Sidebar = styled.div`
  flex: 0 0 250px;
  border-right: 1px solid #ddd;
  padding: 20px;
  @media (max-width: 768px) {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid #ddd;
  }
`;

const TopHeader = styled.div`
  text-align: center;
  margin-bottom: 30px;
`;

const TabButton = styled.button`
  width: 150px;
  flex: 1;
  padding: 8px;
  border: none;
  border-radius: 4px;
  color: ${({active}) => (active ? '#000' : '#fff')};
  cursor: pointer;
  transition: background-color 0.3s;
`;

const CharacterList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  @media (max-width: 768px) {
    align-items: center;
  }
`;

const CharacterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 200px;
  padding: 8px;
  border-radius: 4px;
  color: ${({selected}) => (selected ? '#000' : '#fff')};
  cursor: pointer;
  transition: background-color 0.3s;
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const JobIcon = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;
  border: 1px solid #ddd;
  border-radius: 50%;
  margin-left: 10px;
`;

const CharacterName = styled.div`
  font-size: 1rem;
`;

const DetailPanel = styled.div`
  flex: 1;
  padding: 20px;
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const DetailHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 20px;
`;

const JobIconLarge = styled.img`
  width: 30px;
  height: 30px;
  object-fit: contain;
  border: 1px solid #ddd;
  border-radius: 50%;
`;

const DetailHeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const DetailTitle = styled.h2`
  margin: 0;
  font-size: 1.5rem;
`;

const DetailSubInfo = styled.div`
  font-size: 1rem;
  color: #555;
`;

const DetailTitles = styled.div`
  font-size: 0.9rem;
  color: #888;
  margin-top: 5px;
`;

const Section = styled.div`
  margin-bottom: 20px;
`;

const SectionItems = styled.div`
  margin-bottom: 20px;
`;

const SectionTitle = styled.h3`
  margin-bottom: 10px;
`;

const InfoGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
`;

const InfoItem = styled.div`
  font-size: 0.9rem;
  min-width: 200px;
`;

const EquipmentGrid = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const EquipmentItem = styled.div`
  position: relative;
  width: 40px;
  height: 40px;
  &:hover .tooltip {
    visibility: visible;
    opacity: 1;
  }
`;

const ItemIcon = styled.img`
  width: 40px;
  height: 40px;
  object-fit: contain;
  border: 1px solid #ddd;
  border-radius: 4px;
`;

const Tooltip = styled.div`
  visibility: hidden;
  opacity: 0;
  width: 270px;
  background-color: rgba(0, 0, 0, 0.85);
  color: #fff;
  padding: 10px;
  border-radius: 4px;
  position: absolute;
  z-index: 1;
  bottom: 110%;
  left: 50%;
  transform: translateX(-50%);
  transition: opacity 0.3s;
  font-size: 0.8rem;
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -5px;
    border-width: 5px;
    border-style: solid;
    border-color: rgba(0, 0, 0, 0.85) transparent transparent transparent;
  }
`;

const BeltContainer = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const BeltIcon = styled.img`
  width: 40px;
  height: 40px;
  object-fit: contain;
  border: 1px solid #ddd;
  border-radius: 4px;
`;

const SummonsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 40px);
  gap: 15px;
  align-items: flex-start;
  @media (max-width: 768px) {
    grid-template-columns: repeat(3, 40px);
  }
`;

const SummonItem = styled.div`
  position: relative;
  width: 40px;
  height: 40px;
  &:hover .tooltip {
    visibility: visible;
    opacity: 1;
  }
`;

const SummonIcon = styled.img`
  width: 40px;
  height: 40px;
  object-fit: contain;
  border: 1px solid #ddd;
  border-radius: 4px;
`;

const RestoreButton = styled.button`
  padding: 10px 20px;
  border: none;
  color: #fff;
  border-radius: 4px;
  cursor: pointer;
  background-color: #d9534f;
`;

const PlaceholderText = styled.p`
  font-size: 1.1rem;
  color: #666;
`;
